import type {
  MailIdentityPatchInput,
  MailIdentityTestError,
  MailIdentityTestInput,
  MailIdentityTestResult,
  MailSmtpTls,
  PublicMailIdentity,
} from '@embertime/shared'
import { MAIL_SMTP_ALLOWED_PORTS } from '@embertime/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, CircleAlert, Lock, Plug } from 'lucide-react'
import { useState } from 'react'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { confirm } from '../ui/confirm-dialog'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'
import { Select } from '../ui/select'
import {
  Field,
  IdentityEditSettings,
  identityErrorCode,
  identityErrorText,
} from './identity-form-parts'
import { identitiesQueryKey } from './identity-list'

const TEST_ERRORS: Record<MailIdentityTestError, string> = {
  CONNECT_FAILED: 'Server nicht erreichbar',
  TIMEOUT: 'Server antwortet nicht (Zeitüberschreitung)',
  TLS_FAILED: 'Verschlüsselte Verbindung fehlgeschlagen',
  STARTTLS_UNAVAILABLE: 'Server bietet kein STARTTLS an',
  AUTH_FAILED: 'Anmeldung fehlgeschlagen',
  AUTH_UNSUPPORTED: 'Server bietet keine passende Anmeldung an',
  SENDER_REFUSED: 'Server lehnt diese Absender-Adresse ab',
  PROTOCOL_ERROR: 'Unerwartete Antwort des Servers',
}

/** `TLSv1.3` → `TLS 1.3`; other values unchanged, null → ''. */
export function tlsLabel(tls: string | null): string {
  if (!tls) return ''
  const m = /^TLSv?(\d(?:\.\d)?)$/i.exec(tls.trim())
  return m ? `TLS ${m[1]}` : tls
}

export type SmtpTestView =
  | { ok: true; lines: string[] }
  | { ok: false; title: string; lines: string[] }

/** What the result box shows for a test answer: fixed texts only. */
export function smtpTestView(result: MailIdentityTestResult): SmtpTestView {
  const tls = tlsLabel(result.tls)
  if (!result.error && result.auth === 'ok' && result.senderAccepted === true) {
    return {
      ok: true,
      lines: [
        [tls, 'Anmeldung ok'].filter(Boolean).join(' · '),
        'Server erlaubt diese Absender-Adresse',
      ],
    }
  }
  const code: MailIdentityTestError =
    result.error ??
    (result.auth === 'failed'
      ? 'AUTH_FAILED'
      : result.senderAccepted === false
        ? 'SENDER_REFUSED'
        : 'PROTOCOL_ERROR')
  const lines: string[] = []
  if (tls) lines.push(tls)
  if (result.auth === 'ok') lines.push('Anmeldung ok')
  return { ok: false, title: TEST_ERRORS[code], lines }
}

const DEFAULT_PORT = 587

/** Form „Fremder Postausgang“ (#839, Mockup): SMTP login of a foreign domain.
 * The password is never shown or prefilled; when editing, an empty field keeps
 * the stored one (and is not sent). A changed server needs a new password. */
export function IdentitySmtpSheet({
  open,
  accountId,
  accountLabel,
  identity,
  onBack,
  onClose,
  onSaved,
}: {
  open: boolean
  accountId: string
  accountLabel: string
  identity?: PublicMailIdentity
  onBack: () => void
  onClose: () => void
  onSaved: () => void
}) {
  const qc = useQueryClient()
  const [address, setAddress] = useState(identity?.address ?? '')
  const [displayName, setDisplayName] = useState(identity?.displayName ?? '')
  const [host, setHost] = useState(identity?.smtpHost ?? '')
  const [port, setPort] = useState(String(identity?.smtpPort ?? DEFAULT_PORT))
  const [tls, setTls] = useState<MailSmtpTls>(identity?.smtpTls ?? 'starttls')
  const [user, setUser] = useState(identity?.smtpUser ?? '')
  const [password, setPassword] = useState('')
  const [isDefault, setIsDefault] = useState(identity?.isDefault ?? false)
  const [active, setActive] = useState((identity?.status ?? 'active') === 'active')
  const [error, setError] = useState('')
  const [passwordRequired, setPasswordRequired] = useState(false)
  const [test, setTest] = useState<SmtpTestView | null>(null)
  const invalidate = () => qc.invalidateQueries({ queryKey: identitiesQueryKey(accountId) })

  const hostTrim = host.trim().toLowerCase().replace(/\.$/, '')
  const hostChanged = !!identity && hostTrim !== (identity.smtpHost ?? '')
  // Create, or a new server: the stored password never goes to another host.
  const needsPassword = !identity || hostChanged || !identity.hasPassword
  const portNumber = Number.parseInt(port, 10)
  const portOk = (MAIL_SMTP_ALLOWED_PORTS as readonly number[]).includes(portNumber)

  function fail(err: unknown) {
    if (identityErrorCode(err) === 'MAIL_IDENTITY_PASSWORD_REQUIRED') setPasswordRequired(true)
    setError(identityErrorText(err))
  }

  /** Changed SMTP fields of a saved identity (the patch/test delta). */
  function smtpDelta(): Partial<MailIdentityPatchInput> {
    const delta: Partial<MailIdentityPatchInput> = {}
    if (!identity) return delta
    if (hostTrim !== identity.smtpHost) delta.smtpHost = hostTrim
    if (portNumber !== identity.smtpPort) delta.smtpPort = portNumber
    if (tls !== identity.smtpTls) delta.smtpTls = tls
    if (user.trim() !== identity.smtpUser) delta.smtpUser = user.trim()
    if (password) delta.smtpPassword = password
    return delta
  }

  function checkPassword(): boolean {
    if (needsPassword && !password) {
      setPasswordRequired(true)
      setError(
        identity
          ? 'Neuer Server: bitte das Passwort neu eingeben.'
          : 'Bitte das Passwort eingeben.',
      )
      return false
    }
    return true
  }

  const runTest = useMutation({
    mutationFn: async () => {
      let input: MailIdentityTestInput
      if (identity) {
        const { smtpHost, smtpPort, smtpTls, smtpUser, smtpPassword } = smtpDelta()
        input = { identityId: identity.id }
        if (smtpHost !== undefined) input.smtpHost = smtpHost
        if (smtpPort !== undefined) input.smtpPort = smtpPort
        if (smtpTls !== undefined) input.smtpTls = smtpTls
        if (smtpUser !== undefined) input.smtpUser = smtpUser
        if (smtpPassword !== undefined) input.smtpPassword = smtpPassword
      } else {
        input = {
          address: address.trim().toLowerCase(),
          smtpHost: hostTrim,
          smtpPort: portNumber,
          smtpTls: tls,
          smtpUser: user.trim(),
          smtpPassword: password,
        }
      }
      return api.testMailIdentity(accountId, input)
    },
    onSuccess: (res) => {
      setError('')
      setTest(smtpTestView(res.result))
      if (res.identity) void invalidate()
    },
    onError: (err) => {
      setTest(null)
      fail(err)
    },
  })

  const save = useMutation({
    mutationFn: async () => {
      const name = displayName.trim() || null
      if (!identity) {
        await api.createMailIdentity(accountId, {
          kind: 'external_smtp',
          address: address.trim().toLowerCase(),
          displayName: name,
          smtpHost: hostTrim,
          smtpPort: portNumber,
          smtpTls: tls,
          smtpUser: user.trim(),
          smtpPassword: password,
        })
        return
      }
      const patch: MailIdentityPatchInput = { ...smtpDelta() }
      if (name !== identity.displayName) patch.displayName = name
      const status = active ? 'active' : 'disabled'
      if (status !== identity.status) patch.status = status
      if (active && isDefault !== identity.isDefault) patch.isDefault = isDefault
      if (Object.keys(patch).length > 0) await api.updateMailIdentity(accountId, identity.id, patch)
    },
    onSuccess: () => {
      setPassword('')
      void invalidate()
      onSaved()
    },
    onError: fail,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => api.deleteMailIdentity(accountId, id),
    onSuccess: () => {
      void invalidate()
      onSaved()
    },
    onError: fail,
  })

  async function askDelete() {
    if (!identity) return
    if (
      await confirm({
        title: 'Absender löschen?',
        description: `${identity.address} und der gespeicherte Zugang werden gelöscht. Gesendete Mails bleiben unverändert.`,
        confirmLabel: 'Löschen',
        variant: 'danger',
      })
    )
      remove.mutate(identity.id)
  }

  function pickTls(next: MailSmtpTls) {
    setTls(next)
    // Port suggestion: implicit TLS is 465, STARTTLS is submission 587.
    if (next === 'tls' && portNumber === 587) setPort('465')
    if (next === 'starttls' && portNumber === 465) setPort('587')
  }

  const busy = save.isPending || remove.isPending
  const complete = !!address.trim() && !!hostTrim && portOk && !!user.trim()
  const pwInvalid = passwordRequired && !password

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Fremder Postausgang"
      description={`Absender für „${accountLabel}“`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={identity ? onClose : onBack}>
            Zurück
          </Button>
          <Button type="submit" form="identity-smtp" disabled={busy || !complete}>
            Speichern
          </Button>
        </>
      }
    >
      <form
        id="identity-smtp"
        className="space-y-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (busy || !complete || !checkPassword()) return
          save.mutate()
        }}
      >
        {error && (
          <p role="alert" className="rounded-lg bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        )}
        <Field id="smtp-address" label="Adresse">
          <Input
            id="smtp-address"
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={address}
            disabled={!!identity}
            placeholder="m.muster@partner-beispiel.de"
            onChange={(e) => setAddress(e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="smtp-name" label="Anzeigename">
          <Input
            id="smtp-name"
            value={displayName}
            maxLength={200}
            placeholder="Max Muster"
            onChange={(e) => setDisplayName(e.target.value)}
            className="h-12"
          />
        </Field>
        <div className="grid grid-cols-[minmax(0,1fr)_6.5rem] gap-3">
          <Field id="smtp-host" label="SMTP-Server">
            <Input
              id="smtp-host"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={host}
              placeholder="smtp.partner-beispiel.de"
              onChange={(e) => {
                setHost(e.target.value)
                setTest(null)
              }}
              className="h-12 font-mono"
            />
          </Field>
          <Field id="smtp-port" label="Port">
            <Select
              id="smtp-port"
              required
              value={port}
              aria-invalid={!portOk}
              onChange={(e) => setPort(e.target.value)}
              className={cn('h-12 text-base lg:text-sm', !portOk && 'border-danger/60')}
            >
              {!portOk && <option value={port}>{port}</option>}
              {MAIL_SMTP_ALLOWED_PORTS.map((p) => (
                <option key={p} value={String(p)}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {!portOk && (
          <p className="-mt-2 text-xs text-danger">
            Erlaubt sind die Ports {MAIL_SMTP_ALLOWED_PORTS.join(', ')}.
          </p>
        )}
        <fieldset className="grid grid-cols-2 overflow-hidden rounded-xl border border-border-default">
          <legend className="sr-only">Verschlüsselung</legend>
          {(
            [
              ['starttls', 'STARTTLS'],
              ['tls', 'SSL/TLS'],
            ] as const
          ).map(([value, label]) => (
            <label
              key={value}
              className={cn(
                'flex min-h-12 cursor-pointer items-center justify-center text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-accent/60',
                tls === value
                  ? 'bg-accent/15 text-accent'
                  : 'text-text-secondary hover:bg-bg-overlay',
              )}
            >
              <input
                type="radio"
                name="smtp-tls"
                value={value}
                checked={tls === value}
                onChange={() => pickTls(value)}
                className="sr-only"
              />
              {label}
            </label>
          ))}
        </fieldset>
        <Field id="smtp-user" label="Benutzer">
          <Input
            id="smtp-user"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={user}
            placeholder="m.muster@partner-beispiel.de"
            onChange={(e) => setUser(e.target.value)}
            className="h-12"
          />
        </Field>
        <Field id="smtp-password" label={needsPassword ? 'Passwort' : 'Passwort (optional)'}>
          <Input
            id="smtp-password"
            type="password"
            autoComplete="new-password"
            value={password}
            required={needsPassword}
            aria-invalid={pwInvalid}
            placeholder={identity?.hasPassword && !hostChanged ? 'gespeichert' : ''}
            onChange={(e) => setPassword(e.target.value)}
            className={cn('h-12', pwInvalid && 'border-danger/60')}
          />
          {identity && hostChanged && (
            <p className="text-xs text-warning">Neuer Server: das Passwort bitte neu eingeben.</p>
          )}
          <p className="flex items-center gap-1.5 text-xs text-success">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Wird verschlüsselt gespeichert – nie im Klartext
          </p>
        </Field>
        <Button
          type="button"
          variant="secondary"
          className="h-12 w-full"
          disabled={runTest.isPending || !complete}
          onClick={() => {
            if (checkPassword()) runTest.mutate()
          }}
        >
          <Plug className="h-4 w-4" aria-hidden="true" />
          {runTest.isPending ? 'Teste Verbindung …' : 'Verbindung testen'}
        </Button>
        {test && (
          <div
            role="status"
            className={cn(
              'flex gap-3 rounded-xl border p-3',
              test.ok ? 'border-success/40 bg-success/10' : 'border-danger/40 bg-danger/10',
            )}
          >
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-bg-base',
                test.ok ? 'bg-success' : 'bg-danger',
              )}
            >
              {test.ok ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                <CircleAlert className="h-4 w-4" aria-hidden="true" />
              )}
            </span>
            <div className="min-w-0 space-y-1">
              <p className={cn('text-sm font-semibold', test.ok ? 'text-success' : 'text-danger')}>
                {test.ok ? 'Verbindung steht' : test.title}
              </p>
              {test.lines.map((line) => (
                <p key={line} className="flex items-center gap-1.5 text-xs text-text-secondary">
                  <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
                  {line}
                </p>
              ))}
            </div>
          </div>
        )}
        {identity && (
          <IdentityEditSettings
            isDefault={isDefault}
            active={active}
            disabled={busy}
            onDefaultChange={setIsDefault}
            onActiveChange={(value) => {
              setActive(value)
              if (!value) setIsDefault(false)
            }}
            onDelete={() => void askDelete()}
          />
        )}
      </form>
    </Dialog>
  )
}
